import React, { useCallback, useEffect, useRef } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useWrapPoints } from '../../hooks/useWrapPoints';
import { hasCustomWrapPoints } from '../../lib/textWrap';
import { Button } from '../ui/button';
import { Check, RotateCcw, Spline } from 'lucide-react';

const HANDLE = 8; // px, screen space
const MIN_POINTS = 3;
const RED = 'hsl(0 84% 60%)';

const round4 = (n) => Math.round(n * 10000) / 10000;

/**
 * On-canvas editor for an object's wrap points (Publisher "Edit Wrap Points").
 * Rendered inside the page container in page-pixel space (× zoom). Points are stored in the object's unit space, so
 * this component converts pointer positions through the object's centre / rotation.
 *
 *  • drag a handle          → move the point (text reflows live; one continuous undo step)
 *  • Ctrl+click on an edge  → insert a point there
 *  • Ctrl+click on a handle → remove it (min. 3 points)
 *  • Esc / Done             → leave the mode (handled by the workspace key handler / banner)
 *
 * The mode itself is owned by `state.wrapEditId`; the reducer drops it as soon as the object is deselected,
 * deleted or loses its custom points (e.g. through Undo).
 */
export const WrapPointsEditor = ({ object, zoom, canvasRef }) => {
  const { dispatch, rawDispatch } = useEditor();
  const drag = useRef(null);
  const pts = hasCustomWrapPoints(object) ? object.properties.wrapPoints : [];
  const ptsRef = useRef(pts);
  ptsRef.current = pts;
  const w = Math.max(1, object.width), h = Math.max(1, object.height);
  const cx = (object.x + w / 2) * zoom, cy = (object.y + h / 2) * zoom;
  const rot = object.rotation || 0;

  /** Pointer → object unit space. */
  const toUnit = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / zoom, py = (e.clientY - rect.top) / zoom;
    const dx = px - (object.x + w / 2), dy = py - (object.y + h / 2);
    const r = (-rot * Math.PI) / 180;
    const lx = dx * Math.cos(r) - dy * Math.sin(r);
    const ly = dx * Math.sin(r) + dy * Math.cos(r);
    return [round4(lx / w + 0.5), round4(ly / h + 0.5)];
  }, [canvasRef, zoom, object.x, object.y, w, h, rot]);

  /** Discrete change (add / remove) → goes through the history-aware dispatch. */
  const commit = useCallback((next) => dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [object.id], props: { wrapPoints: next } } }), [dispatch, object.id]);
  /** Streaming change during a drag → no history entries; the snapshot is taken once at drag start. */
  const stream = useCallback((next) => rawDispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [object.id], props: { wrapPoints: next } } }), [rawDispatch, object.id]);

  /** Window-level drag so the gesture survives re-renders (handles are re-created when points change). One undo step per drag. */
  const startDrag = useCallback((index, { snapshot = true } = {}) => {
    if (snapshot) dispatch({ type: 'PUSH_HISTORY' });
    const onMove = (e) => {
      if (!drag.current) return;
      const p = toUnit(e);
      stream(ptsRef.current.map((q, k) => (k === drag.current.i ? p : q)));
    };
    const stop = () => {
      drag.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    drag.current = { i: index };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  }, [toUnit, stream, dispatch]);

  useEffect(() => () => { drag.current = null; }, []);

  const onHandleDown = (i) => (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault(); // also suppresses the compat mousedown, so the object underneath does not start dragging
    if (e.ctrlKey || e.metaKey) {
      if (pts.length > MIN_POINTS) commit(pts.filter((_, k) => k !== i));
      return;
    }
    startDrag(i);
  };
  const onEdgeDown = (i) => (e) => {
    if (e.button !== 0) return;
    if (!(e.ctrlKey || e.metaKey)) return; // plain clicks on the outline fall through to the object (move it)
    e.stopPropagation();
    e.preventDefault();
    const p = toUnit(e);
    const next = pts.slice();
    next.splice(i + 1, 0, p);
    // add-and-drag in one gesture (one undo step), like Publisher
    dispatch({ type: 'PUSH_HISTORY' });
    stream(next);
    startDrag(i + 1, { snapshot: false });
  };

  if (pts.length < MIN_POINTS) return null;
  const local = pts.map(([u, v]) => [(u - 0.5) * w * zoom, (v - 0.5) * h * zoom]);
  const polygon = local.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const hs = HANDLE;

  return (
    <svg className="absolute inset-0" style={{ width: '100%', height: '100%', overflow: 'visible', zIndex: 1000, pointerEvents: 'none' }} data-testid="wrap-points-editor" aria-label="Wrap points" role="group">
      <g transform={`translate(${cx} ${cy}) rotate(${rot})`}>
        <polygon points={polygon} fill="hsl(0 84% 60% / 0.06)" stroke={RED} strokeWidth="1.25" strokeDasharray="5 3" vectorEffect="non-scaling-stroke" data-testid="wrap-points-outline" />
        {local.map(([x, y], i) => {
          const [nx, ny] = local[(i + 1) % local.length];
          return <line key={`e-${i}`} x1={x} y1={y} x2={nx} y2={ny} stroke="transparent" strokeWidth="12" style={{ pointerEvents: 'stroke', cursor: 'copy' }} onPointerDown={onEdgeDown(i)} data-testid={`wrap-edge-${i}`} />;
        })}
        {local.map(([x, y], i) => (
          <rect
            key={`p-${i}`}
            x={x - hs / 2}
            y={y - hs / 2}
            width={hs}
            height={hs}
            fill="#ffffff"
            stroke={RED}
            strokeWidth="1.5"
            style={{ pointerEvents: 'all', cursor: 'move' }}
            onPointerDown={onHandleDown(i)}
            data-testid={`wrap-point-${i}`}
          />
        ))}
      </g>
    </svg>
  );
};

/** Floating mode banner (rendered outside the scrolling page so it is never clipped). */
export const WrapPointsBanner = ({ object }) => {
  const { rawDispatch } = useEditor();
  const { reset } = useWrapPoints();
  const count = hasCustomWrapPoints(object) ? object.properties.wrapPoints.length : 0;
  const exit = () => rawDispatch({ type: 'SET_WRAP_EDIT', payload: null });
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 top-2 z-[60] flex items-center gap-2 rounded-full border border-border bg-popover/90 backdrop-blur-md px-3 py-1 shadow-md text-[12px] whitespace-nowrap max-w-[calc(100%-1rem)]"
      role="status"
      data-testid="wrap-points-banner"
    >
      <Spline className="h-3.5 w-3.5 text-[hsl(0_84%_60%)]" aria-hidden="true" />
      <span className="text-foreground font-medium">Editing wrap points</span>
      <span className="text-muted-foreground hidden lg:inline" data-testid="wrap-points-banner-hint">· drag a point to move it · Ctrl+click an edge to add · Ctrl+click a point to remove</span>
      <span className="text-muted-foreground" data-testid="wrap-points-count">{count} pts</span>
      <Button variant="ghost" size="sm" className="h-6 px-2 text-[12px]" onMouseDown={(e) => e.preventDefault()} onClick={() => reset([object.id])} data-testid="wrap-points-reset"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset</Button>
      <Button size="sm" className="h-6 px-2 text-[12px]" onMouseDown={(e) => e.preventDefault()} onClick={exit} data-testid="wrap-points-done"><Check className="h-3.5 w-3.5 mr-1" /> Done</Button>
    </div>
  );
};
